import { buildQueryString, request } from "../../client"

export type DistrictStatistics = {
  properties: {
    total: number
    single_family: number
    multi_family: number
    unknown: number
  }
  contact_points: {
    email: number
    mobile: number
    fixed_line: number
  }
  customers: {
    total: number
    email_reachable: number
    message_reachable: number
    phone_reachable: number
  }
}

const readCounts = <Key extends string>(
  value: unknown,
  keys: readonly Key[],
): Record<Key, number> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Invalid district statistics response")
  }

  const record = value as Record<string, unknown>
  const entries = keys.map((key) => {
    const count = record[key]

    if (typeof count !== "number" || !Number.isSafeInteger(count) || count < 0) {
      throw new Error("Invalid district statistics response")
    }

    return [key, count] as const
  })

  return Object.fromEntries(entries) as Record<Key, number>
}

export const fetchDistrictStatistics = async (organizationId: string, signal?: AbortSignal) => {
  const response = await request<unknown>(
    `/district/statistics${buildQueryString({ organization_id: organizationId })}`,
    { signal },
  )

  if (!response || typeof response !== "object" || Array.isArray(response)) {
    throw new Error("Invalid district statistics response")
  }

  const record = response as Record<string, unknown>
  const statistics: DistrictStatistics = {
    properties: readCounts(record.properties, [
      "total",
      "single_family",
      "multi_family",
      "unknown",
    ]),
    contact_points: readCounts(record.contact_points, ["email", "mobile", "fixed_line"]),
    customers: readCounts(record.customers, [
      "total",
      "email_reachable",
      "message_reachable",
      "phone_reachable",
    ]),
  }
  const { properties, customers } = statistics

  if (
    properties.single_family + properties.multi_family + properties.unknown !== properties.total ||
    customers.email_reachable > customers.total ||
    customers.message_reachable > customers.total ||
    customers.phone_reachable > customers.total
  ) {
    throw new Error("Invalid district statistics response")
  }

  return statistics
}
